'use client'

import { env } from 'next-runtime-env'
import { createListCollection } from '@chakra-ui/react'
import {
  Accordion,
  Box,
  Container,
  Flex,
  HStack,
  Heading,
  Icon,
  Input,
  Link,
  Select,
  Stack,
  Tabs,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { forwardRef, useEffect, useMemo, useState } from 'react'
import { FaPlay, FaSearch } from 'react-icons/fa'
import type {
  IRDocument,
  IRResultQAPair,
  IRThemeConfig,
  InvestorRelationsConfigContextType,
} from '@/utils/investorRelations'
import type { Attachment } from '@/utils/types'
import {
  DocumentYearFilter,
  getAvailableDocumentYears,
  getPublishedYear,
} from './DocumentYearFilter'
import InvestorRelationsDocumentViewer from '@/components/InvestorRelations/InvestorRelationsDocumentViewer'

interface ResultsSectionProps {
  results: IRDocument[]
  isLoading: boolean
  isPreview?: boolean
  organisationId?: string
  deepLinkedDocumentId?: string | null
  onDeepLinkedDocumentClose?: (sectionId: string) => void
  onDocumentOpen?: (sectionId: string, documentId: string) => void
  useContextFunction: () => InvestorRelationsConfigContextType
}

const CONTENT_LABELS = new Set([
  'video',
  'q&a',
  'qa',
  'presentation',
  'document',
])
const NEXT_PUBLIC_ASSIDUOUS_API_URL = env('NEXT_PUBLIC_ASSIDUOUS_API_URL')

function formatResultDate(dateInput: string | null | undefined): string {
  if (!dateInput || dateInput === '[Not published]') {
    return 'Not published'
  }

  const date = new Date(dateInput)
  if (Number.isNaN(date.getTime())) {
    return 'Not published'
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function getVisibleLabels(result: IRDocument): string[] {
  return result.labels.filter((item) => !CONTENT_LABELS.has(item.toLowerCase()))
}

function sanitizeSearchInput(input: string): string {
  const MAX_SEARCH_LENGTH = 20
  return input.trim().slice(0, MAX_SEARCH_LENGTH).replace(/[<>]/g, '')
}

function getVideoEmbedUrl(url: string, origin?: string | null): string | null {
  try {
    const parsed = new URL(url)
    const searchParams = new URLSearchParams()
    if (origin) {
      searchParams.set('origin', origin)
    }

    if (
      parsed.hostname.includes('youtube.com') &&
      parsed.searchParams.get('v')
    ) {
      const videoId = parsed.searchParams.get('v')
      return `https://www.youtube.com/embed/${videoId}${
        searchParams.toString() ? `?${searchParams.toString()}` : ''
      }`
    }
    if (parsed.hostname === 'youtu.be') {
      return `https://www.youtube.com/embed${parsed.pathname}${
        searchParams.toString() ? `?${searchParams.toString()}` : ''
      }`
    }
    if (parsed.hostname.includes('vimeo.com')) {
      const videoId = parsed.pathname.split('/').filter(Boolean).pop()
      if (videoId) {
        return `https://player.vimeo.com/video/${videoId}`
      }
    }
  } catch {
    return null
  }

  return null
}

const panelStyles = {
  bg: 'white',
  borderRadius: '24px',
  border: '1px solid',
  borderColor: 'gray.200',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
}

export const ResultsSection = forwardRef<HTMLElement, ResultsSectionProps>(
  function ResultsSection(
    {
      results,
      isLoading,
      isPreview,
      organisationId,
      deepLinkedDocumentId = null,
      onDeepLinkedDocumentClose,
      onDocumentOpen,
      useContextFunction,
    },
    ref
  ) {
    const { config } =
      useContextFunction() as InvestorRelationsConfigContextType
    const themeConfig = ((config?.themeConfig as IRThemeConfig) ||
      {}) as IRThemeConfig
    const primaryColor = themeConfig.primaryColor || 'teal.600'
    const textColor = themeConfig.textColor || 'gray.800'
    const fontFamily = themeConfig.fontStyle
    const resultsExtras = config?.documentConfig?.reports?.resultsExtras
    const questions: IRResultQAPair[] = resultsExtras?.qa || []
    const [selectedAttachmentForPreview, setSelectedAttachmentForPreview] =
      useState<Attachment | null>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedType, setSelectedType] = useState<string>('all')
    const [selectedYear, setSelectedYear] = useState<number | null>(null)
    const attachmentPreviewer = useDisclosure()

    const sortedResults = useMemo(() => {
      return [...(results || [])].sort((a, b) => {
        const aTime =
          a.publishedDate && a.publishedDate !== '[Not published]'
            ? new Date(a.publishedDate).getTime()
            : 0
        const bTime =
          b.publishedDate && b.publishedDate !== '[Not published]'
            ? new Date(b.publishedDate).getTime()
            : 0

        if (aTime !== bTime) {
          return bTime - aTime
        }

        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
    }, [results])

    const availableYears = useMemo(
      () => getAvailableDocumentYears(sortedResults),
      [sortedResults]
    )
    const activeYear = availableYears.includes(selectedYear ?? Number.NaN)
      ? selectedYear
      : availableYears[0] ?? null

    const companySlug =
      config?.companySlugPublished || config?.companySlugPreview || ''

    const uploadedVideoSrc =
      resultsExtras?.video.publishedUrl ||
      (resultsExtras?.video.mode === 'upload' &&
      resultsExtras.video.attachmentId
        ? isPreview && organisationId
          ? `/api/proxy/admin/investor-relations/${organisationId}/documents/reports/${resultsExtras.video.attachmentId}`
          : `${NEXT_PUBLIC_ASSIDUOUS_API_URL}/investor-relations/${companySlug}/documents/reports/${resultsExtras.video.attachmentId}`
        : null)
    const embeddedVideoUrl =
      resultsExtras?.video.mode === 'url' && resultsExtras.video.url
        ? getVideoEmbedUrl(
            resultsExtras.video.url,
            typeof window !== 'undefined' ? window.location.origin : null
          )
        : null
    const hasVideo =
      Boolean(uploadedVideoSrc) ||
      Boolean(embeddedVideoUrl) ||
      Boolean(resultsExtras?.video.mode === 'url' && resultsExtras?.video.url)
    const hasQuestions = questions.length > 0
    const hasResultDocuments = sortedResults.length > 0
    const filteredResults = useMemo(() => {
      let filtered = sortedResults

      if (availableYears.length > 1 && activeYear !== null) {
        filtered = filtered.filter(
          (result) => getPublishedYear(result.publishedDate) === activeYear
        )
      }

      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        filtered = filtered.filter((result) => {
          const labels = getVisibleLabels(result)
          return (
            result.fileName.toLowerCase().includes(query) ||
            labels.some((label) => label.toLowerCase().includes(query))
          )
        })
      }

      if (selectedType !== 'all') {
        filtered = filtered.filter((result) =>
          getVisibleLabels(result).some(
            (label) => label.toLowerCase() === selectedType.toLowerCase()
          )
        )
      }

      return filtered
    }, [
      activeYear,
      availableYears.length,
      searchQuery,
      selectedType,
      sortedResults,
    ])
    const uniqueLabels = useMemo(() => {
      const labels = new Set<string>(['all'])
      for (const result of sortedResults) {
        for (const label of getVisibleLabels(result)) {
          labels.add(label)
        }
      }
      return Array.from(labels).sort()
    }, [sortedResults])
    const typeOptions = useMemo(
      () =>
        createListCollection({
          items: [
            { value: 'all', label: 'All Labels' },
            ...uniqueLabels
              .filter((label) => label !== 'all')
              .map((label) => ({
                value: label,
                label,
              })),
          ],
        }),
      [uniqueLabels]
    )

    const handleView = (attachment: IRDocument) => {
      setSelectedAttachmentForPreview(attachment as unknown as Attachment)
      attachmentPreviewer.onOpen()
      onDocumentOpen?.('results', attachment.attachmentId)
    }

    useEffect(() => {
      if (!deepLinkedDocumentId || attachmentPreviewer.open) return

      const result = sortedResults.find(
        (item) => item.attachmentId === deepLinkedDocumentId
      )
      if (!result) return

      setSelectedAttachmentForPreview(result as unknown as Attachment)
      attachmentPreviewer.onOpen()
    }, [
      attachmentPreviewer,
      attachmentPreviewer.open,
      deepLinkedDocumentId,
      sortedResults,
    ])

    return (
      <>
        <Box
          ref={ref}
          as="section"
          id="results"
          bg={`${primaryColor}10`}
          minH="100vh"
          display="flex"
          alignItems="center"
          py={{ base: 12, md: 20 }}
        >
          <Container maxW="6xl">
            <Stack gap={{ base: 6, md: 10 }}>
              <VStack gap={4} textAlign="center">
                <Heading
                  as="h2"
                  size={{ base: 'lg', md: '5xl' }}
                  color={primaryColor}
                  fontWeight="bold"
                  fontFamily={fontFamily}
                >
                  Results
                </Heading>
                <DocumentYearFilter
                  years={availableYears}
                  selectedYear={activeYear}
                  onYearChange={setSelectedYear}
                  primaryColor={primaryColor}
                  fontFamily={fontFamily}
                  sectionTitle="results"
                />
              </VStack>

              {isLoading ? (
                <Box
                  bg="white"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="sm"
                  p={{ base: 8, md: 10 }}
                >
                  <Text color={textColor} fontFamily={fontFamily}>
                    Loading results...
                  </Text>
                </Box>
              ) : !hasResultDocuments && !hasVideo && !hasQuestions ? (
                <Box
                  bg="white"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="sm"
                  p={{ base: 8, md: 10 }}
                  textAlign="center"
                >
                  <Heading
                    as="h3"
                    fontSize="xl"
                    mb={2}
                    color={primaryColor}
                    fontFamily={fontFamily}
                  >
                    Results coming soon
                  </Heading>
                  <Text color={textColor} fontFamily={fontFamily}>
                    Check back soon for the latest results release, video and
                    Q&amp;A.
                  </Text>
                </Box>
              ) : (
                <Stack gap={6}>
                  {(hasVideo || hasQuestions) && (
                    <Box {...panelStyles} overflow="hidden">
                      <Box p={{ base: 4, md: 6 }}>
                        <Tabs.Root
                          defaultValue={hasVideo ? 'video' : 'qa'}
                          lazyMount
                          unmountOnExit={false}
                          variant="enclosed"
                        >
                          <Tabs.List mb={6} w="fit-content">
                            {hasVideo && (
                              <Tabs.Trigger
                                value="video"
                                color={textColor}
                                _selected={{
                                  color: primaryColor,
                                }}
                              >
                                Video
                              </Tabs.Trigger>
                            )}
                            <Tabs.Trigger
                              value="qa"
                              color={textColor}
                              _selected={{
                                color: primaryColor,
                              }}
                            >
                              Q&amp;A
                            </Tabs.Trigger>
                          </Tabs.List>

                          {hasVideo && (
                            <Tabs.Content value="video">
                              <Stack gap={4}>
                                {uploadedVideoSrc &&
                                resultsExtras?.video.fileType?.startsWith(
                                  'video/'
                                ) ? (
                                  <Box
                                    borderRadius="md"
                                    overflow="hidden"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    bg="black"
                                  >
                                    <Box asChild>
                                      <video
                                        controls
                                        src={uploadedVideoSrc}
                                        style={{
                                          width: '100%',
                                          minHeight: '520px',
                                        }}
                                      >
                                        <track
                                          kind="captions"
                                          src="data:text/vtt;charset=utf-8,WEBVTT"
                                          srcLang="en"
                                          label="English captions"
                                        />
                                      </video>
                                    </Box>
                                  </Box>
                                ) : embeddedVideoUrl ? (
                                  <Box
                                    borderRadius="md"
                                    overflow="hidden"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    bg="black"
                                  >
                                    <Box asChild>
                                      <iframe
                                        src={embeddedVideoUrl}
                                        title="Results video"
                                        style={{
                                          width: '100%',
                                          minHeight: '520px',
                                          border: 0,
                                        }}
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                      />
                                    </Box>
                                  </Box>
                                ) : (
                                  <Flex
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    bg="gray.900"
                                    minH={{ base: '240px', md: '520px' }}
                                    align="center"
                                    justify="center"
                                    direction="column"
                                    gap={4}
                                  >
                                    <Flex
                                      align="center"
                                      justify="center"
                                      w="72px"
                                      h="72px"
                                      borderRadius="full"
                                      bg="whiteAlpha.900"
                                      color={primaryColor}
                                      boxShadow="md"
                                    >
                                      <Icon as={FaPlay} boxSize={6} />
                                    </Flex>
                                    <Text
                                      color="white"
                                      fontFamily={fontFamily}
                                      fontSize="sm"
                                      textAlign="center"
                                      maxW="220px"
                                    >
                                      Add a results video in document
                                      configuration to show it here.
                                    </Text>
                                  </Flex>
                                )}

                                {resultsExtras?.video.mode === 'url' &&
                                  resultsExtras.video.url &&
                                  !embeddedVideoUrl && (
                                    <Link
                                      href={resultsExtras.video.url}
                                      color={primaryColor}
                                      target="_blank"
                                      rel="noreferrer"
                                      fontWeight="medium"
                                      fontFamily={fontFamily}
                                    >
                                      Open video link
                                    </Link>
                                  )}
                              </Stack>
                            </Tabs.Content>
                          )}

                          <Tabs.Content value="qa">
                            {hasQuestions ? (
                              <Accordion.Root
                                collapsible
                                defaultValue={[questions[0]?.id].filter(
                                  Boolean
                                )}
                                borderTop="1px solid"
                                borderColor="gray.200"
                              >
                                {questions.map((item) => (
                                  <Accordion.Item
                                    key={item.id}
                                    value={item.id}
                                    border="0"
                                    borderBottom="1px solid"
                                    borderColor="gray.200"
                                  >
                                    <Accordion.ItemTrigger
                                      py={4}
                                      cursor="pointer"
                                      _hover={{
                                        '& [data-part="question-text"]': {
                                          textDecoration: 'underline',
                                        },
                                      }}
                                    >
                                      <Box flex="1" textAlign="left">
                                        <Text
                                          data-part="question-text"
                                          fontSize={{ base: 'lg', md: 'xl' }}
                                          fontWeight="semibold"
                                          color={textColor}
                                          fontFamily={fontFamily}
                                          lineHeight="1.4"
                                        >
                                          {item.question}
                                        </Text>
                                      </Box>
                                      <Accordion.ItemIndicator
                                        color={textColor}
                                      />
                                    </Accordion.ItemTrigger>
                                    <Accordion.ItemContent>
                                      <Accordion.ItemBody pt={0} pb={4}>
                                        <Text
                                          color="gray.600"
                                          fontFamily={fontFamily}
                                          fontSize={{ base: 'sm', md: 'md' }}
                                          lineHeight="1.75"
                                          pr={{ md: 6 }}
                                        >
                                          {item.answer}
                                        </Text>
                                      </Accordion.ItemBody>
                                    </Accordion.ItemContent>
                                  </Accordion.Item>
                                ))}
                              </Accordion.Root>
                            ) : (
                              <Flex
                                minH={{ base: '220px', md: '320px' }}
                                align="center"
                                justify="center"
                                px={6}
                                textAlign="center"
                              >
                                <Text
                                  color={primaryColor}
                                  fontFamily={fontFamily}
                                  fontSize={{ base: 'sm', md: 'md' }}
                                >
                                  No questions available at the moment. Please
                                  check back later for updates
                                </Text>
                              </Flex>
                            )}
                          </Tabs.Content>
                        </Tabs.Root>
                      </Box>
                    </Box>
                  )}

                  {hasResultDocuments && (
                    <Stack gap={4}>
                      <HStack gap={4} flexWrap="wrap">
                        <Box
                          position="relative"
                          maxW={{ base: 'full', md: '300px' }}
                          minW={0}
                          w={{ base: 'full', md: '300px' }}
                        >
                          <Icon
                            as={FaSearch}
                            position="absolute"
                            left="8px"
                            top="50%"
                            transform="translateY(-50%)"
                            color="gray.400"
                            boxSize="12px"
                          />
                          <Input
                            placeholder="Search"
                            value={searchQuery}
                            onChange={(event) =>
                              setSearchQuery(
                                sanitizeSearchInput(event.target.value)
                              )
                            }
                            bg="white"
                            borderColor="gray.300"
                            borderRadius="8px"
                            fontSize="10px"
                            h="28px"
                            lineHeight="1"
                            pl="25px"
                            pr="6px"
                            type="search"
                          />
                        </Box>
                        <Select.Root
                          collection={typeOptions}
                          value={[selectedType]}
                          onValueChange={(details) => {
                            setSelectedType(details.value[0])
                          }}
                          w={{ base: 'full', md: '200px' }}
                        >
                          <Select.HiddenSelect />
                          <Select.Control>
                            <Select.Trigger bg="white" borderColor="gray.300">
                              <Select.ValueText>
                                {selectedType === 'all'
                                  ? 'All Labels'
                                  : selectedType}
                              </Select.ValueText>
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                              <Select.Indicator />
                            </Select.IndicatorGroup>
                          </Select.Control>
                          <Select.Positioner>
                            <Select.Content>
                              {typeOptions.items.map((item) => (
                                <Select.Item key={item.value} item={item}>
                                  <Select.ItemText>
                                    {item.label}
                                  </Select.ItemText>
                                  <Select.ItemIndicator />
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select.Positioner>
                        </Select.Root>
                      </HStack>

                      {filteredResults.length === 0 ? (
                        <Box
                          bg="white"
                          borderRadius="lg"
                          boxShadow="sm"
                          p={{ base: 6, md: 8 }}
                          textAlign="center"
                          borderWidth="1px"
                          borderColor="gray.200"
                        >
                          <Text color="gray.700" fontFamily={fontFamily}>
                            No results documents match your filters.
                          </Text>
                        </Box>
                      ) : (
                        filteredResults.map((result) => {
                          const visibleLabels = getVisibleLabels(result)
                          return (
                            <Box
                              key={result.attachmentId}
                              borderWidth="1px"
                              borderRadius="lg"
                              bg="white"
                              p={4}
                              boxShadow="sm"
                              cursor="pointer"
                              _hover={{
                                shadow: 'md',
                                transform: 'translateY(-2px)',
                              }}
                              onClick={() => handleView(result)}
                              transition="all 0.2s"
                            >
                              <Flex
                                direction={{ base: 'column', md: 'row' }}
                                gap={4}
                                align={{ base: 'flex-start', md: 'center' }}
                                justify="space-between"
                              >
                                <Box flex="1" minW={0} w="full">
                                  <HStack gap={3} mb={2} flexWrap="wrap">
                                    <Text
                                      fontSize="sm"
                                      color="gray.600"
                                      fontFamily={fontFamily}
                                    >
                                      {formatResultDate(result.publishedDate)}
                                    </Text>
                                    {visibleLabels.map((label) => (
                                      <Box
                                        key={label}
                                        px={2}
                                        py={1}
                                        borderRadius="md"
                                        bg={`${primaryColor}1A`}
                                        color={primaryColor}
                                        fontSize="xs"
                                        fontWeight="medium"
                                      >
                                        {label}
                                      </Box>
                                    ))}
                                  </HStack>
                                  <Heading
                                    as="h3"
                                    size="md"
                                    color={textColor}
                                    fontWeight="semibold"
                                    fontFamily={fontFamily}
                                    maxW="full"
                                    overflowWrap="anywhere"
                                    wordBreak="break-word"
                                    lineHeight="1.35"
                                  >
                                    {result.fileName}
                                  </Heading>
                                </Box>
                              </Flex>
                            </Box>
                          )
                        })
                      )}
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          </Container>
        </Box>
        <InvestorRelationsDocumentViewer
          attachment={selectedAttachmentForPreview}
          isOpen={attachmentPreviewer.open}
          anonymousMode={true}
          companySlug={companySlug}
          section="reports"
          isPreview={isPreview}
          organisationId={organisationId}
          companyLogoUrl={config?.companyConfig?.logoUrl}
          onClose={() => {
            const closedAttachmentId =
              selectedAttachmentForPreview?.attachmentId
            attachmentPreviewer.onClose()
            setSelectedAttachmentForPreview(null)
            if (closedAttachmentId === deepLinkedDocumentId) {
              onDeepLinkedDocumentClose?.('results')
            }
          }}
        />
      </>
    )
  }
)
